import React from 'react';
import styled from 'astroturf/react';

import { isDownloadRead } from '../openerIntent';
import type { ArmSpec } from '../armRegistry';
import { getWorkContext } from '../workContext';

// The modal tree stays mounted across close/reopen (dialog CSS toggle) and
// React reconciles a same-type slot node in place, so without a fresh key a
// reopened modal would not replay the checklist reveal.
let openSequence = 0;

const BLACK = 'var(--brand-colors-neutral-black, #141413)';

function checklist(pageCount: number): string[] {
  return [
    'Paper found',
    `${pageCount} ${pageCount === 1 ? 'page' : 'pages'} ready`,
    'Formatted to download',
  ];
}

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-xxs-8, 8px);
  margin-bottom: var(--spacing-xs-16, 16px);
  font-family: var(--type-font-family-sans-serif, 'DM Sans', sans-serif);
`;

const Heading = styled.span`
  font-size: 15px;
  font-weight: 700;
  color: ${BLACK};
  text-align: center;
`;

const List = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Item = styled.li`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 500;
  color: ${BLACK};
  opacity: 0;
  animation: dp-item-in 220ms ease var(--item-delay, 0ms) both;

  @keyframes dp-item-in {
    from {
      opacity: 0;
      transform: translateY(3px);
    }
    to {
      opacity: 1;
      transform: none;
    }
  }
  @media (prefers-reduced-motion: reduce) {
    opacity: 1;
    animation: none;
  }
`;

const CheckCircle = styled.span`
  flex: 0 0 auto;
  box-sizing: border-box;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: ${BLACK};
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  line-height: 1;
  transform: scale(0);
  animation: dp-check-pop 200ms cubic-bezier(0.2, 0.8, 0.3, 1.4)
    var(--item-delay, 0ms) forwards;

  @keyframes dp-check-pop {
    to {
      transform: scale(1);
    }
  }
  @media (prefers-reduced-motion: reduce) {
    transform: scale(1);
    animation: none;
  }
`;

const CueText = styled.span`
  margin-top: 2px;
  font-size: 13px;
  font-weight: 600;
  color: ${BLACK};
  text-align: center;
`;

const DownloadReadyPreflight: React.FC<{ pageCount: number }> = ({
  pageCount,
}) => (
  <Panel>
    <Heading>Your PDF is ready</Heading>
    <List role="list">
      {checklist(pageCount).map((label, index) => (
        <Item
          key={label}
          role="listitem"
          style={{ '--item-delay': `${index * 140}ms` } as React.CSSProperties}
        >
          <CheckCircle
            aria-hidden="true"
            style={
              { '--item-delay': `${index * 140}ms` } as React.CSSProperties
            }
          >
            ✓
          </CheckCircle>
          {label}
        </Item>
      ))}
    </List>
    <CueText>Sign up for free to save it</CueText>
  </Panel>
);

export const downloadPreflightSpec: ArmSpec = {
  stamp: (ctx) => {
    if (!isDownloadRead(ctx.detail)) return undefined;
    const pageCount = getWorkContext()?.pageCount;
    const renders = pageCount != null && pageCount > 0;
    return { render_state: renders ? 'rendered' : 'fallback' };
  },
  slots: (ctx) => {
    if (!isDownloadRead(ctx.detail)) return undefined;
    const pageCount = getWorkContext()?.pageCount;
    if (pageCount == null || pageCount <= 0) return undefined;
    openSequence += 1;
    return {
      aboveHeader: (
        <DownloadReadyPreflight key={openSequence} pageCount={pageCount} />
      ),
    };
  },
};
